import { createPresence, type PresenceResult } from "@solid-primitives/presence";
import { type AnimationParams, animate } from "animejs";
import { type Accessor, type Component, createEffect, createSignal, type JSX, on, onCleanup, Show } from "solid-js";

export type AnimationProps = {
  ref: (el: HTMLElement) => void;
  isMounted: Accessor<boolean>;
  isVisible: Accessor<boolean>;
};

/** The scale a menu or popover grows from. An app can set --st-menu-scale on
 * :root for a subtler open; without it menus keep the 0.9 they always had. */
export const menuScale = (): number =>
  Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--st-menu-scale")) || 0.9;

/** An app opts into native motion by setting --st-motion-native on :root: a
 * quicker leave, a reversal from wherever the element stands, and only the
 * fade under reduced motion. Without it, motion is what it always was. */
const nativeMotion = (): boolean =>
  getComputedStyle(document.documentElement).getPropertyValue("--st-motion-native").trim() !== "";

const reducedMotion = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Reduced motion keeps the fade, the one change that reads as appearing rather
// than moving.
const opacityOnly = (params: AnimationParams): AnimationParams =>
  "opacity" in params ? { opacity: params.opacity } : {};

// A [from, to] pair restarts from `from`; reversing a half-run animation from
// there jumps, so a reversal animates from wherever the element is now.
const toCurrent = (params: AnimationParams): AnimationParams =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, Array.isArray(value) ? value[value.length - 1] : value])
  ) as AnimationParams;

export const AnimateChildren: Component<{
  show: boolean;
  children: (animationProps: AnimationProps) => JSX.Element;
  /**
   * Time in milliseconds for the entire animation
   * @default 200
   */
  duration?: number;
  enter: AnimationParams;
  exit: AnimationParams;

  presence?: PresenceResult<boolean>;
}> = (props) => {
  const duration = () => props.duration ?? 200;

  const [refs, setRefs] = createSignal<HTMLElement[]>([]);

  const registerRef = (el: HTMLElement) => {
    setRefs((prev) => [...prev, el]);
  };

  const { isVisible, isMounted } =
    props.presence ??
    createPresence(() => props.show, {
      transitionDuration: duration,
    });

  createEffect(() => {
    if (!isMounted()) {
      setRefs([]);
      return;
    }
  });

  // Set while an animation is mid-flight, so the next one knows it reverses it.
  let running = false;
  createEffect(
    on(isVisible, (visible) => {
      const els = refs();
      if (els.length === 0) return;

      const native = nativeMotion();
      let params = visible ? props.enter : props.exit;
      if (native && running) params = toCurrent(params);
      if (native && reducedMotion()) params = opacityOnly(params);
      running = true;
      const animation = animate(els, {
        // Leaving is quicker than arriving: the user has already moved on.
        duration: visible || !native ? duration() : Math.round(duration() * 0.65),
        ...params,
        onComplete: () => {
          running = false;
        },
      });
      onCleanup(() => animation.cancel());
    })
  );

  return <Show when={isMounted()}>{props.children({ ref: registerRef, isMounted, isVisible })}</Show>;
};
